import { regenerateMenuStyles } from '@/components/session/RegenerateMenu.styles';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SUMMARY_STYLE_OPTIONS, type SummaryStyle } from '@/types/summaryStyle';
import { ChevronDown, Sparkles } from 'lucide-react';
import { Link } from 'wouter';

interface RegenerateMenuProps {
  /** Called with no style for a plain regenerate, or with the chosen style key. */
  onSelect: (style?: SummaryStyle) => void;
  disabled?: boolean;
  iconOnly?: boolean;
}

const RegenerateMenuItems = ({ onSelect, disabled }: Pick<RegenerateMenuProps, 'onSelect' | 'disabled'>) => (
  <>
    <DropdownMenuItem disabled={disabled} onSelect={() => onSelect()}>
      Endurgera
    </DropdownMenuItem>
    <DropdownMenuSeparator />
    {SUMMARY_STYLE_OPTIONS.map((option) => (
      <DropdownMenuItem key={option.style} disabled={disabled} onSelect={() => onSelect(option.style)}>
        {option.label}
      </DropdownMenuItem>
    ))}
    <DropdownMenuSeparator />
    <DropdownMenuItem asChild className={regenerateMenuStyles.settingsHint}>
      <Link href="/settings">
        <span>
          Hægt er að stilla þetta varanlega hér: <span className={regenerateMenuStyles.settingsLink}>Stillingar</span>
        </span>
      </Link>
    </DropdownMenuItem>
  </>
);

/**
 * "Endurgera" button that opens a menu of regenerate options. Every option
 * regenerates from the transcript; the style options add a dedicated prompt
 * instruction for that one run. The footer links to Settings, where a style can be saved as the default.
 */
export const RegenerateMenu = ({ onSelect, disabled, iconOnly = false }: RegenerateMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        size={iconOnly ? 'icon' : 'sm'}
        variant="ghost"
        disabled={disabled}
        className={iconOnly ? regenerateMenuStyles.iconTrigger : regenerateMenuStyles.trigger}
        aria-label="Endurgera"
        title={iconOnly ? 'Endurgera' : undefined}
      >
        <Sparkles className="h-4 w-4" />
        {!iconOnly && (
          <>
            Endurgera
            <ChevronDown className="h-3 w-3 opacity-60" />
          </>
        )}
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <RegenerateMenuItems onSelect={onSelect} disabled={disabled} />
    </DropdownMenuContent>
  </DropdownMenu>
);
